/**
 * Owner: Masa Bayu
 * File: bka-deployment-release.ts
 * Date:Time: 2026-09-22 18:30
 * Description: Slice D — BKA Add Book→QXRead freeze · identity · deploy record · fail-closed.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { assertTimestamp } from './guards';
import { BKA_ADAM_FEATURE_FLAG } from './bka-adam-types';
import { BKA_MANUSCRIPT_FEATURE_FLAG, BKA_PDF_FEATURE_FLAG } from './bka-manuscript-types';
import { BKA_POST_HANDOFF_FEATURE_FLAG } from './bka-post-handoff-types';
import { BKA_WAVE_A_FEATURE_FLAG } from './bka-wave-a-types';
export const QX_BKA_ADD_BOOK_RELEASE_ID = 'qx-bka-add-book@1.0.0';
export const QX_BKA_ADD_BOOK_QXP_ARCHITECTURE_LOCK = 'QXP v1.0.0';
export const QX_BKA_ADD_BOOK_PRODUCTION_RELEASE_REF = 'qx-bka-add-book-production@1.0.0';
export const QX_BKA_DEFAULT_PACKAGE_PINS = Object.freeze([
    Object.freeze({ name: '@qx/qxread-core', version: '0.1.0' }),
    Object.freeze({ name: '@qx/qxwriter', version: '0.1.0' }),
    Object.freeze({ name: '@qx/qxw-core', version: '0.1.0' }),
    Object.freeze({ name: '@qx/qxp-core', version: '0.1.0' }),
    Object.freeze({ name: '@qx/qxreader', version: '0.1.0' }),
]);
export const QX_BKA_DEFAULT_BUILD_ARTIFACTS = Object.freeze([
    'packages/qxread-core/dist',
    'packages/qxw-core/dist',
    'packages/qxp-core/dist',
    'packages/qxreader/dist',
    'apps/qxwriter/dist',
]);
const BKA_FLAG_IDS = Object.freeze([
    BKA_WAVE_A_FEATURE_FLAG.id,
    BKA_MANUSCRIPT_FEATURE_FLAG.id,
    BKA_PDF_FEATURE_FLAG.id,
    BKA_ADAM_FEATURE_FLAG.id,
    BKA_POST_HANDOFF_FEATURE_FLAG.id,
]);
function allFlagsProductionOff() {
    return (BKA_WAVE_A_FEATURE_FLAG.defaultEnabledInProduction === false &&
        BKA_MANUSCRIPT_FEATURE_FLAG.defaultEnabledInProduction === false &&
        BKA_PDF_FEATURE_FLAG.defaultEnabledInProduction === false &&
        BKA_ADAM_FEATURE_FLAG.defaultEnabledInProduction === false &&
        BKA_POST_HANDOFF_FEATURE_FLAG.defaultEnabledInProduction === false);
}
/** Freeze tested BKA Add Book→QXRead artifact — Slice D only · flags remain off. */
export function freezeBkaAddBookRelease(input) {
    assertNoForbiddenFoundationKey(input ?? {}, 'BKA Slice D release freeze');
    if (!allFlagsProductionOff()) {
        throw new Error('BKA Slice D freeze requires all BKA flags production-off');
    }
    const frozenAt = input?.frozenAt ?? new Date().toISOString();
    assertTimestamp(frozenAt, 'BKA Slice D frozenAt');
    const packages = Object.freeze([...(input?.packages ?? QX_BKA_DEFAULT_PACKAGE_PINS)].map((pin) => Object.freeze({ ...pin })));
    if (packages.length === 0) {
        throw new Error('BKA Slice D freeze requires package pins');
    }
    for (const pin of packages) {
        if (pin.name.trim().length === 0 || pin.version.trim().length === 0) {
            throw new Error('BKA Slice D package pin name/version required');
        }
    }
    return Object.freeze({
        releaseId: QX_BKA_ADD_BOOK_RELEASE_ID,
        frozenAt,
        frozen: true,
        program: 'BKA Add Book → QXRead',
        priorGatesClosed: Object.freeze([
            'Add Book E2E',
            'Post-Add Book Handoff',
            'Add Book → QXRead Production Verification',
            'Deployment / Production-GA Authorization',
        ]),
        packages,
        buildArtifacts: Object.freeze([
            ...(input?.buildArtifacts ?? QX_BKA_DEFAULT_BUILD_ARTIFACTS),
        ]),
        featureFlagsProductionOff: true,
        featureFlagIds: BKA_FLAG_IDS,
        qxpArchitectureLock: QX_BKA_ADD_BOOK_QXP_ARCHITECTURE_LOCK,
        architectureFrozen: true,
        noSourceModificationAfterFreeze: true,
        sliceDOnly: true,
        autoEnableFlags: false,
        autoProductionGa: false,
        mutatesQxw: false,
        mutatesQxp: false,
        inventsAuthority: false,
        doc03Undisturbed: true,
    });
}
export function assertBkaAddBookManifestFrozen(manifest) {
    return (manifest.frozen === true &&
        manifest.releaseId === QX_BKA_ADD_BOOK_RELEASE_ID &&
        manifest.qxpArchitectureLock === QX_BKA_ADD_BOOK_QXP_ARCHITECTURE_LOCK &&
        manifest.architectureFrozen === true &&
        manifest.featureFlagsProductionOff === true &&
        manifest.autoEnableFlags === false &&
        manifest.autoProductionGa === false &&
        manifest.mutatesQxw === false &&
        manifest.mutatesQxp === false &&
        manifest.inventsAuthority === false &&
        manifest.doc03Undisturbed === true);
}
function check(id, ok, detail) {
    return Object.freeze({ id, ok, detail });
}
/** Verify freeze identity · fail-closed on mismatch. */
export function verifyBkaAddBookReleaseIdentity(input) {
    assertNoForbiddenFoundationKey(input, 'BKA Slice D release identity');
    const manifest = input.manifest;
    const observed = input.observedPackages ?? QX_BKA_DEFAULT_PACKAGE_PINS;
    const pinMap = new Map(manifest.packages.map((p) => [p.name, p.version]));
    const depsOk = observed.every((pin) => pinMap.get(pin.name) === pin.version);
    const observedId = input.observedReleaseId ?? manifest.releaseId;
    const checks = Object.freeze([
        check('manifest_frozen', assertBkaAddBookManifestFrozen(manifest), `releaseId=${manifest.releaseId}`),
        check('release_id_match', observedId === manifest.releaseId, `observed=${observedId}`),
        check('package_pins', depsOk, 'observed pins match frozen manifest'),
        check('qxp_lock', manifest.qxpArchitectureLock === QX_BKA_ADD_BOOK_QXP_ARCHITECTURE_LOCK, 'QXP v1.0.0 undisturbed'),
        check('flags_production_off', allFlagsProductionOff(), 'Slice F not auto-completed'),
        check('typecheck', input.typecheckClean, 'typecheck clean'),
        check('bka_domain_regression', input.bkaDomainTestsPass, 'BKA domain PASS'),
        check('handoff_regression', input.handoffTestsPass, 'post-handoff PASS'),
        check('architecture_frozen', manifest.architectureFrozen &&
            manifest.mutatesQxw === false &&
            manifest.mutatesQxp === false &&
            manifest.inventsAuthority === false, 'no QXW/QXP/authority mutation'),
        check('doc03_undisturbed', manifest.doc03Undisturbed === true, 'QX-DOC-03 open'),
    ]);
    return Object.freeze({
        releaseId: manifest.releaseId,
        checks,
        allPass: checks.every((c) => c.ok),
        mutatesQxw: false,
        mutatesQxp: false,
    });
}
/**
 * Deploy tested BKA artifact into immutable deployment record.
 * Host attestation optional — if present must match freeze releaseId.
 */
export function deployBkaAddBookArtifact(input) {
    assertNoForbiddenFoundationKey(input, 'BKA Slice D deploy artifact');
    if (!assertBkaAddBookManifestFrozen(input.manifest)) {
        throw new Error('BKA Slice D deploy requires frozen manifest');
    }
    const deployedAt = input.deployedAt ?? new Date().toISOString();
    assertTimestamp(deployedAt, 'BKA Slice D deployedAt');
    const hostId = input.hostDeployedReleaseId === undefined
        ? null
        : input.hostDeployedReleaseId;
    const hostAttestationPresent = hostId !== null && hostId.length > 0;
    const identityConsistent = !hostAttestationPresent || hostId === input.manifest.releaseId;
    return Object.freeze({
        releaseId: QX_BKA_ADD_BOOK_RELEASE_ID,
        productionReleaseRef: QX_BKA_ADD_BOOK_PRODUCTION_RELEASE_REF,
        deployedAt,
        artifactDeployed: true,
        deployedReleaseId: input.manifest.releaseId,
        hostDeployedReleaseId: hostId,
        hostAttestationPresent,
        identityConsistent,
        sliceD: true,
        sliceFCompleted: false,
        sliceGCompleted: false,
        prerequisites: Object.freeze([
            'Add Book E2E CLOSED',
            'Post-Add Book Handoff CLOSED',
            'Add Book → QXRead Production Verification CLOSED',
            'Deployment / Production-GA Authorization ACCEPTED',
            'Slice D freeze complete',
            'feature flags remain production-off (Slice F not completed)',
        ]),
        rollbackConditions: Object.freeze([
            'release identity mismatch',
            'host attestation mismatch',
            'Add Book → handoff verification FAIL',
            'Source/ADAM → QXP bypass',
            'smoke suite FAIL',
            'BKA domain regression FAIL',
        ]),
        mutatesQxw: false,
        mutatesQxp: false,
    });
}
//# sourceMappingURL=bka-deployment-release.js.map